import { ExperimentFlags, getExperimentValue } from "data/experiment"
import { isBrowser } from "utils/isBrowser"

import Cookies from "js-cookie"
import {
  bodyBenefits,
  bodyContainer,
  bodyHeading,
  bodyPerks,
  bodyPrice,
  bodyPriceExplainer,
  bodyPriceExplainerContainer,
  buttonContainer,
  container,
  innerBodyContainer,
  ribbonContainer,
  ribbonExtraClass,
  ribbonStyle,
} from "./MembershipOption.css"
import { colours } from "@shared/ui.design-system"
import { useCurrencySymbol } from "hooks"
import { ButtonCta } from "packages/acquisition/components/chakra-components/CMS/ButtonCta"
import type { CMSPageProps } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils"
import { cmsPageClick } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils"
import { useState } from "react"
import { addMembershipById } from "lib/api"
import { useLandingPageContext } from "packages/acquisition/components/chakra-components/LandingPage/LandingPageProvider"
import type { BannerCTA } from "types/cms"

export type LandingPageMembershipOptions = {
  ribbonContent: string
  ribbonColor: string
  heading: string
  priceExplainer: string
  price: number
  cta: BannerCTA
  sku: string
  content: string
}

type Props = CMSPageProps &
  LandingPageMembershipOptions & {
    onClose?: () => void
    setConfirmation?: React.Dispatch<React.SetStateAction<boolean>>
    disable?: boolean
  }

export const MembershipOption: React.FC<Props> = ({
  ribbonContent,
  ribbonColor,
  trackingModule,
  heading,
  priceExplainer,
  price,
  cta,
  setConfirmation,
  onClose,
  sku,
  content,
  disable,
}) => {
  const currencySymbol = useCurrencySymbol()
  const [loading, setLoading] = useState(false)
  const cookiesByName = isBrowser() ? Cookies.getJSON() : {}
  const { setATBConfirmation } = useLandingPageContext()

  const ribbonColorMap = {
    pink: colours.accent.soft,
    grey: colours.secondary.warm,
    mango: colours.warning.medium,
  }

  const useMemberPropositionContent =
    getExperimentValue(
      cookiesByName,
      ExperimentFlags.PAGE_EXPERIMENT_CONTENT_FOR_DISCOVERY_JOURNEY
    ) === "1"

  const handleClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (trackingModule) {
      cmsPageClick({
        ...trackingModule,
        moduleContentDetails: sku,
      })
      setLoading(true)
    }
    addMembershipById(sku)
      .then(() => {
        if (setConfirmation && onClose) {
          onClose()
          setConfirmation(true)
        } else {
          setATBConfirmation(true)
        }
      })
      .catch(() =>
        console.error(
          `Could not add membership:${sku} to cache in MembershipCard`
        )
      )
      .finally(() => setLoading(false))
  }

  return (
    <div
      className={`${container} ${
        ribbonContent && !disable ? ribbonExtraClass : ""
      }`}
    >
      {ribbonContent && !disable && (
        <div
          className={ribbonContainer}
          style={{
            backgroundColor: ribbonColorMap[ribbonColor] ?? ribbonColorMap.pink,
          }}
        >
          <div
            className={ribbonStyle}
            dangerouslySetInnerHTML={{
              __html: ribbonContent,
            }}
          />
        </div>
      )}
      <div className={bodyContainer}>
        <div className={innerBodyContainer}>
          <h2 className={bodyHeading}>{heading}</h2>
          <div className={bodyPriceExplainerContainer}>
            <div style={{ width: "100%" }}>
              <span className={bodyPrice}>
                {price === 0 ? "free" : `${currencySymbol}${price}`}{" "}
              </span>
              <div
                className={bodyPriceExplainer}
                dangerouslySetInnerHTML={{
                  __html: priceExplainer,
                }}
              />
            </div>
          </div>

          <div>
            <p className={bodyBenefits}>
              {useMemberPropositionContent
                ? "What you'll get:"
                : "Membership Benefits:"}
            </p>

            <div
              className={bodyPerks}
              dangerouslySetInnerHTML={{
                __html: content,
              }}
            />
          </div>
        </div>
        <div className={buttonContainer}>
          {cta && (
            <ButtonCta
              trackingModule={
                trackingModule ?? {
                  modulePosition: 0,
                  moduleContent: "",
                  moduleId: "",
                }
              }
              {...cta}
              link="atb"
              customOnClickHandler={handleClick}
              buttonProps={{ w: "100% !important" }}
              isLoading={loading}
            />
          )}
        </div>
      </div>
    </div>
  )
}
